<template>
    <div class="filter-group" :class="{ 'nested-group': depth > 0 }">
        <div v-if="depth > 0" class="group-header">
            <select v-model="group.logicalOperator" class="filter-logical">
                <option value="and">И</option>
                <option value="or">ИЛИ</option>
            </select>
            <span class="group-bracket">(</span>
            <button class="remove-group" @click="removeGroup">×</button>
        </div>

        <div class="group-body">
            <div v-for="(item, index) in group.children" :key="item.id" class="filter-row">
                <template v-if="item.type === 'condition'">
                    <select v-if="index > 0" v-model="item.logicalOperator" class="filter-logical">
                        <option value="and">И</option>
                        <option value="or">ИЛИ</option>
                    </select>
                    <span v-else class="logical-label">Где</span>

                    <select
                        v-model="item.field"
                        class="filter-field"
                        @change="updateCondition(item)"
                    >
                        <option value="">Выберите поле</option>
                        <option v-for="f in filtersConfig" :key="f.field" :value="f.field">
                            {{ f.name }}
                        </option>
                    </select>

                    <select
                        v-if="showOperator(item)"
                        v-model="item.operator"
                        class="filter-operator"
                        @change="updateCondition(item)"
                    >
                        <option value="eq">Равно</option>
                        <option value="range">В диапазоне (±)</option>
                        <option value="ne">Не равно</option>
                        <option value="gt">Больше</option>
                        <option value="gte">Больше или равно</option>
                        <option value="lt">Меньше</option>
                        <option value="lte">Меньше или равно</option>
                    </select>

                    <div class="value-container">
                        <SearchableSelect
                            v-if="getInputType(item.field) === 'searchable-select'"
                            v-model="item.value"
                            :options="getOptionsForField(item.field)"
                            :placeholder="`Выберите ${getFieldName(item.field)}`"
                            class="filter-value"
                        />

                        <select
                            v-else-if="getInputType(item.field) === 'select'"
                            v-model="item.value"
                            class="filter-value"
                        >
                            <option value="">Выберите {{ getFieldName(item.field) }}</option>
                            <option
                                v-for="option in getOptionsForField(item.field)"
                                :key="option.key || option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>

                        <select
                            v-else-if="getFilterType(item.field) === 'boolean'"
                            v-model="item.value"
                            class="filter-value"
                        >
                            <option value="">Выберите</option>
                            <option :value="true">Да</option>
                            <option :value="false">Нет</option>
                        </select>

                        <input
                            v-else
                            v-model="item.value"
                            :type="getFilterType(item.field) === 'number' ? 'number' : 'text'"
                            class="filter-value"
                            :placeholder="getPlaceholder(item.field)"
                        />
                    </div>

                    <div
                        v-if="item.operator === 'range' && shouldShowTolerance(item)"
                        class="tolerance-group"
                    >
                        <span>±</span>
                        <input
                            v-model="item.tolerance"
                            type="number"
                            min="0"
                            class="filter-tolerance"
                            placeholder="Погрешность"
                        />
                    </div>

                    <button class="remove-filter" @click="removeCondition(index)">×</button>
                </template>

                <FilterGroup
                    v-else-if="item.type === 'group'"
                    :group="item"
                    :filters-config="filtersConfig"
                    :depth="depth + 1"
                    :path="[...path, index]"
                    @add-condition="$emit('add-condition', $event)"
                    @add-group="$emit('add-group', $event)"
                    @remove-condition="$emit('remove-condition', $event)"
                    @remove-group="$emit('remove-group', $event)"
                />
            </div>
        </div>

        <div v-if="depth > 0" class="group-footer">
            <span class="group-bracket">)</span>
            <div class="group-actions">
                <button class="btn small" @click="$emit('add-condition', path)">+ Условие</button>
                <button class="btn small" @click="$emit('add-group', path)">+ Группа</button>
            </div>
        </div>
    </div>
</template>

<script>
    import SearchableSelect from './SearchableSelect.vue'
    import api from '../services/api'
    import { reactive, toRefs } from 'vue'

    export default {
        components: { SearchableSelect },
        props: {
            group: {
                type: Object,
                required: true
            },
            filtersConfig: {
                type: Array,
                default: () => []
            },
            depth: {
                type: Number,
                default: 0
            },
            path: {
                type: Array,
                default: () => []
            }
        },
        emits: ['add-condition', 'add-group', 'remove-condition', 'remove-group'],
        setup(props, { emit }) {
            const state = reactive({
                cachedOptions: {},
                loadingOptions: false,
                optionsLoaded: false
            })

            const getFilterConfig = field => {
                return props.filtersConfig.find(f => f.field === field) || {}
            }

            const getFilterType = field => {
                const filter = getFilterConfig(field)
                return filter.type || 'text'
            }

            const getInputType = field => {
                const filter = getFilterConfig(field)
                return filter.inputType || 'text'
            }

            const getFieldName = field => {
                const filter = getFilterConfig(field)
                return filter.name || field
            }

            const getPlaceholder = field => {
                const filter = getFilterConfig(field)
                return `Введите ${filter.name || field}`
            }

            const getOptionsForField = field => {
                if (!field) return []

                const filterConfig = getFilterConfig(field)
                const cached = state.cachedOptions[field] || []

                if (filterConfig.enum_mapping) {
                    return Object.entries(filterConfig.enum_mapping).map(([label, value]) => ({
                        value: value,
                        label: label,
                        key: label
                    }))
                }

                return cached
            }

            const fetchOptionsForField = async (field, endpoint) => {
                try {
                    const options = await api.get(endpoint)

                    const filterConfig = getFilterConfig(field)
                    let formattedOptions = []

                    if (filterConfig.enumMapping) {
                        formattedOptions = options.map(opt => {
                            const value = typeof opt === 'object' ? opt.value : opt
                            const label =
                                filterConfig.enumMapping[value] ||
                                (typeof opt === 'object' ? opt.label : String(opt))
                            return { value, label }
                        })
                    } else {
                        formattedOptions = options.map(opt => {
                            if (typeof opt === 'string' || typeof opt === 'number') {
                                return { value: opt, label: String(opt) }
                            }
                            return opt
                        })
                    }

                    state.cachedOptions[field] = formattedOptions
                    return formattedOptions
                } catch (error) {
                    console.error(`Failed to fetch options for ${field}:`, error)
                    state.cachedOptions[field] = []
                    return []
                }
            }

            const preloadOptions = async () => {
                if (
                    state.loadingOptions ||
                    !props.filtersConfig ||
                    props.filtersConfig.length === 0
                ) {
                    return
                }

                state.loadingOptions = true
                const optionPromises = []

                props.filtersConfig.forEach(filter => {
                    if (filter.optionsEndpoint && !state.cachedOptions[filter.field]) {
                        optionPromises.push(
                            fetchOptionsForField(filter.field, filter.optionsEndpoint)
                        )
                    } else if (filter.options && Array.isArray(filter.options)) {
                        const formattedOptions = filter.options.map(opt => {
                            if (typeof opt === 'string' || typeof opt === 'number') {
                                return { value: opt, label: String(opt) }
                            }
                            return opt
                        })
                        state.cachedOptions[filter.field] = formattedOptions
                    } else if (filter.enumMapping) {
                        const formattedOptions = Object.entries(filter.enumMapping).map(
                            ([label, value]) => ({
                                value: value, // Use the numeric value
                                label: label // Keep the string label
                            })
                        )
                        state.cachedOptions[filter.field] = formattedOptions
                    }
                })

                await Promise.all(optionPromises)
                state.optionsLoaded = true
                state.loadingOptions = false
            }

            const showOperator = condition => {
                if (!condition.field) return true
                const inputType = getInputType(condition.field)
                const filterType = getFilterType(condition.field)

                return !(
                    filterType === 'boolean' ||
                    inputType === 'select' ||
                    inputType === 'searchable-select'
                )
            }

            const shouldShowTolerance = condition => {
                if (!condition.field) return false
                const type = getFilterType(condition.field)
                return type === 'number' && condition.operator === 'range'
            }

            const updateCondition = condition => {
                if (!condition.field) return
                const fieldConfig = getFilterConfig(condition.field)

                if (fieldConfig.optionsEndpoint && !state.cachedOptions[condition.field]) {
                    fetchOptionsForField(condition.field, fieldConfig.optionsEndpoint)
                }

                // Convert the value using enum mapping if available
                if (fieldConfig.enumMapping && condition.value) {
                    // Map the display value to the enum value
                    condition.value = fieldConfig.enumMapping[condition.value]
                }

                if (condition.operator !== 'range') {
                    condition.tolerance = null
                } else if (fieldConfig?.defaultTolerance) {
                    condition.tolerance = fieldConfig.defaultTolerance
                }
            }

            const removeCondition = index => {
                emit('remove-condition', [...props.path, index])
            }

            const removeGroup = () => {
                emit('remove-group', props.path)
            }

            return {
                ...toRefs(state),
                getFilterConfig,
                getFilterType,
                getInputType,
                getFieldName,
                getPlaceholder,
                getOptionsForField,
                preloadOptions,
                showOperator,
                shouldShowTolerance,
                updateCondition,
                removeCondition,
                removeGroup
            }
        },
        watch: {
            'group.logicalOperator'(newVal) {
                this.group.children.forEach(child => {
                    if (child.type === 'group') {
                        child.logicalOperator = newVal
                    }
                })
            },
            filtersConfig: {
                immediate: true,
                deep: true,
                handler(newVal) {
                    if (newVal && newVal.length > 0 && !this.optionsLoaded) {
                        this.preloadOptions()
                    }
                }
            }
        },
        mounted() {
            if (this.filtersConfig && this.filtersConfig.length > 0 && !this.optionsLoaded) {
                this.preloadOptions()
            }
        }
    }
</script>

<style>
    .filter-group {
        border-left: 3px solid #4a6cf7;
        padding-left: 15px;
        margin: 10px 0;
        position: relative;
    }

    .nested-group {
        border-left-color: #e0e6f0;
    }

    .group-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
    }

    .group-footer {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
    }

    .group-bracket {
        font-size: 24px;
        font-weight: bold;
        color: #4a6cf7;
    }

    .group-actions {
        display: flex;
        gap: 5px;
        margin-left: 10px;
    }

    .btn.small {
        padding: 5px 10px;
        font-size: 12px;
        background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .btn.small:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .filter-row {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
        margin-bottom: 15px;
        padding: 10px;
        background-color: #f8fafc;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        padding-right: 40px;
    }

    .remove-group {
        background: none;
        border: none;
        color: #e53e3e;
        font-size: 1.5em;
        cursor: pointer;
        padding: 5px;
        line-height: 1;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
    }

    .remove-group:hover {
        background: #ffeaea;
    }

    .filter-logical {
        width: 70px;
        padding: 10px 5px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        text-align: center;
        height: 42px;
        box-sizing: border-box;
        background: #fff;
    }

    .logical-label {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #718096;
        width: 70px;
        height: 42px;
    }

    .filter-field {
        flex: 1;
        min-width: 160px;
        padding: 10px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
        background: #fff;
        height: 42px;
    }

    .filter-operator {
        width: 140px;
        padding: 10px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
        background: #fff;
        height: 42px;
    }

    .value-container {
        flex: 1;
        min-width: 150px;
    }

    .value-container input,
    .value-container select {
        width: 100%;
        height: 100%;
        padding: 10px;
        box-sizing: border-box;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        background: #fff;
        height: 42px;
    }

    .tolerance-group {
        display: flex;
        align-items: center;
        gap: 5px;
    }

    .filter-tolerance {
        width: 100px;
        padding: 10px 15px;
        border: 1px solid #d0d9e7;
        border-radius: 8px;
        font-size: 15px;
        box-sizing: border-box;
        background: #fff;
        height: 42px;
    }

    .remove-filter {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #e53e3e;
        font-size: 1.5em;
        cursor: pointer;
        padding: 5px;
        line-height: 1;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
    }

    .remove-filter:hover {
        background: #ffeaea;
    }

    @media (max-width: 768px) {
        .filter-row {
            flex-direction: column;
            align-items: stretch;
            padding-right: 40px;
        }

        .filter-field,
        .filter-operator,
        .value-container,
        .tolerance-group {
            width: 100%;
        }

        .filter-logical,
        .logical-label {
            width: 100%;
            margin-bottom: 10px;
        }
    }
</style>
